import { dateRangeTest } from '../../../../utils/documentValidation';

/**
 * Regras puras do passo Sócios da alteração (25/09), sem React: o que cada tipo marcado libera
 * no card do sócio, o que trava em quem continua e as contas da cessão de quotas. Usadas pelo
 * StepSocios, pelo AcaoSocioBlock e pelo QuadroAntesDepois.
 */

/** O que o card do sócio libera, conforme os tipos de alteração marcados. */
export interface ILiberacoesDoPassoSocios {
  /** Botões "Sair" e "Adicionar sócio". */
  entrarSair: boolean;
  /** Bloco "Cede quotas a" de quem continua e altera. */
  cessao: boolean;
  /** Aspecto "Quotas" e o campo de quotas de quem continua. */
  quotas: boolean;
  /** Aspecto "Vínculo". */
  vinculo: boolean;
  /** Aspectos de qualificação: endereço, nome, profissão, estado civil e representante. */
  qualificacao: boolean;
}

/**
 * Tabela de liberação do passo Sócios:
 * - entrarSair: Quadro societário ou Administração;
 * - cessao: Quadro societário;
 * - quotas: Quadro societário ou Capital;
 * - vinculo: Quadro societário ou Administração;
 * - qualificacao: Quadro societário, Administração ou Dados de sócio.
 */
export const liberacoesDoPassoSocios = (
  tipos: readonly string[] | null | undefined,
): ILiberacoesDoPassoSocios => {
  const marcado = (tipo: string) => (tipos ?? []).includes(tipo);
  const quadro = marcado('quadro_societario');
  const administracao = marcado('administracao');
  return {
    entrarSair: quadro || administracao,
    cessao: quadro,
    quotas: quadro || marcado('capital'),
    vinculo: quadro || administracao,
    qualificacao: quadro || administracao || marcado('dados_socio'),
  };
};

/** O aspecto de "Alterar" (quotas, vinculo, endereco...) está liberado pelos tipos marcados? */
export const aspectoLiberado = (aspecto: string, liberacoes: ILiberacoesDoPassoSocios): boolean => {
  if (aspecto === 'quotas') return liberacoes.quotas;
  if (aspecto === 'vinculo') return liberacoes.vinculo;
  return liberacoes.qualificacao;
};

type DocDoSocio = { cpf?: string | null; cnpj?: string | null } | null | undefined;

/** Par do sócio na leitura do vigente (por CPF/CNPJ), ou null quando a leitura não o trouxe. */
export const parNoVigente = <T>(
  socio: DocDoSocio,
  vigentePorDoc: Record<string, T> | null | undefined,
): T | null => {
  const doc = String(socio?.cpf || socio?.cnpj || '').replace(/\D/g, '');
  if (!doc || !vigentePorDoc) return null;
  return vigentePorDoc[doc] ?? null;
};

/** Vínculo de quem administra: sócio-administrador ou administrador não sócio. */
export const ehAdministrador = (vinculo: unknown): boolean =>
  vinculo === 'socio_administrador' || vinculo === 'administrador';

/**
 * A leitura do vigente trouxe administrador? Sem nenhum, o vínculo lido está errado (toda LTDA
 * tem administrador): fica editável em quem continua, sem virar "Alterar" (R3, 25/09).
 */
export const vigenteTemAdministrador = (
  vigentePorDoc: Record<string, { tipo_vinculo?: string | null }> | null | undefined,
): boolean => Object.values(vigentePorDoc ?? {}).some(v => ehAdministrador(v?.tipo_vinculo));

type VinculoLido = { tipo_vinculo?: string | null } | null | undefined;

/**
 * A leitura trouxe administrador? Olha o quadro lido inteiro: o administrador não sócio vem sem
 * CPF/CNPJ e fica fora do vigentePorDoc. Sem o quadro lido, usa o vigentePorDoc (R3, 28/09).
 */
export const leituraTemAdministrador = (
  quadroLido: ReadonlyArray<VinculoLido> | null | undefined,
  vigentePorDoc: Record<string, { tipo_vinculo?: string | null }> | null | undefined,
): boolean =>
  quadroLido
    ? quadroLido.some(s => ehAdministrador(s?.tipo_vinculo))
    : vigenteTemAdministrador(vigentePorDoc);

type IdentidadeLida = { cpf?: string | null; cnpj?: string | null; nome?: string | null };

// CPF/CNPJ só com letra e dígito (o CNPJ novo tem letra), em caixa alta.
const documentoDe = (s: IdentidadeLida | null | undefined): string =>
  String(s?.cpf || s?.cnpj || '')
    .replace(/[^A-Za-z0-9]/g, '')
    .toUpperCase();

// Sem acento: o NFD separa a letra da marca (U+0300 a U+036F) e a marca sai.
const semAcento = (texto: string): string =>
  Array.from(texto.normalize('NFD'))
    .filter(c => {
      const ponto = c.codePointAt(0) ?? 0;
      return ponto < 0x300 || ponto > 0x36f;
    })
    .join('');

/** Nome sem acento, pontuação nem caixa: "Ána  Silva" e "ANA SILVA" casam. */
export const nomeAchatado = (nome: string | null | undefined): string =>
  semAcento(String(nome ?? ''))
    .replace(/[^A-Za-z0-9]+/g, ' ')
    .trim()
    .toUpperCase();

const mesmaPessoa = (a: IdentidadeLida, b: IdentidadeLida): boolean => {
  const doc = documentoDe(a);
  if (doc && doc === documentoDe(b)) return true;
  const nome = nomeAchatado(a.nome);
  return nome.length > 0 && nome === nomeAchatado(b.nome);
};

/**
 * Linha do quadro lido que é deste sócio: a da mesma posição quando a ordem bate (confirmada por
 * CPF/CNPJ ou nome achatado); senão a que casa pelo documento e, por fim, pelo nome. Null quando
 * a leitura não traz o sócio. As travas do card leem esta linha, não o formulário (28/09).
 */
export const linhaDaLeitura = <T extends IdentidadeLida>(
  socio: IdentidadeLida | null | undefined,
  indice: number,
  quadroLido: ReadonlyArray<T | null | undefined> | null | undefined,
): T | null => {
  if (!socio || !quadroLido) return null;
  const naPosicao = quadroLido[indice];
  if (naPosicao && mesmaPessoa(socio, naPosicao)) return naPosicao;
  const doc = documentoDe(socio);
  const porDocumento = doc ? quadroLido.find(l => !!l && documentoDe(l) === doc) : undefined;
  if (porDocumento) return porDocumento;
  const nome = nomeAchatado(socio.nome);
  if (!nome) return null;
  return quadroLido.find(l => !!l && nomeAchatado(l.nome) === nome) ?? null;
};

const nascimentoNoIntervalo = dateRangeTest({
  min: '1900-01-01',
  max: 'today',
  name: 'dt-nascimento-range',
});

/** Data de nascimento que o schema aceita (1900 a hoje). Vazia não conta: fica editável. */
export const dataDeNascimentoValida = (valor: string | null | undefined): boolean =>
  !!valor && nascimentoNoIntervalo.test(valor);

interface IDestinoDaCessao {
  socio_ordem?: number | null;
  qtd?: number | string | null;
}

interface ISocioDaCessao {
  nome?: string | null;
  acao?: string | null;
  redistribuicao_quotas?: ReadonlyArray<IDestinoDaCessao | null | undefined> | null;
}

type SociosDaCessao = ReadonlyArray<ISocioDaCessao | null | undefined>;

/** Soma do que o sócio cede na própria cessão (só quantidades positivas). */
export const somaCedida = (socio: ISocioDaCessao | null | undefined): number =>
  (socio?.redistribuicao_quotas ?? []).reduce(
    (acc, d) => acc + (Number(d?.qtd) > 0 ? Number(d?.qtd) : 0),
    0,
  );

/**
 * Quanto o sócio pode ceder no ato: quem sai, só o vigente; quem altera, o vigente e o que recebe
 * no mesmo ato (recebidoPorOrdemDe). A mesma conta do schema, do card e do quadro (28/09).
 */
export const disponivelParaCeder = (
  acao: string | null | undefined,
  quotasVigente: number,
  recebidoNoAto: number,
): number => (acao === 'sair' ? quotasVigente : quotasVigente + recebidoNoAto);

/** Quanto cada sócio recebe por cessão (de quem sai ou de quem altera e cede), por ordem. */
export const recebidoPorOrdemDe = (socios: SociosDaCessao): Record<number, number> => {
  const recebido: Record<number, number> = {};
  socios.forEach(s => {
    // Quem sai redistribui tudo; quem continua (Alterar + Quotas) pode ceder parte.
    if (s?.acao !== 'sair' && s?.acao !== 'alterar') return;
    (s.redistribuicao_quotas ?? []).forEach(d => {
      const ordem = d?.socio_ordem;
      const qtd = Number(d?.qtd);
      if (ordem != null && Number.isFinite(qtd) && qtd > 0) {
        recebido[ordem] = (recebido[ordem] ?? 0) + qtd;
      }
    });
  });
  return recebido;
};

/**
 * Por que o destino escolhido na cessão não vale mais, ou null: ele saiu do quadro depois de
 * escolhido. A cessão fica como o operador digitou; ele troca o destino (item 5, 25/09).
 */
export const motivoDestinoInvalido = (
  socios: SociosDaCessao,
  ordem: number | null | undefined,
): string | null => {
  if (ordem == null) return null;
  const destino = socios[ordem];
  if (!destino) return 'O sócio escolhido não está mais no quadro';
  if (destino.acao !== 'sair') return null;
  const nome = destino.nome || `Sócio ${ordem + 1}`;
  return `${nome} sai do quadro`;
};

/** Destinos que não recebem mais cessão, por ordem: o aviso que a linha da cessão mostra. */
export const destinosInvalidosDaCessao = (socios: SociosDaCessao): Record<number, string> => {
  const invalidos: Record<number, string> = {};
  socios.forEach((_, ordem) => {
    const motivo = motivoDestinoInvalido(socios, ordem);
    if (motivo) invalidos[ordem] = motivo;
  });
  return invalidos;
};
